import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import Tippy from "@tippyjs/react";

import { Button, IconButton, InfoPlate } from "../..";
import { ArrowDown, ArrowUp, MoreIcon } from "../../Icons";
import useDropdown from "../../../hooks/useDropdown";

import styles from "../CollapsableCard.module.scss";

export interface ActionOption {
  value: string;
  label: string;
  disabled?: boolean;
  onClick: () => void;
}

interface Props {
  id: string;
  title?: string;
  opened?: boolean;
  isTitlePlate?: boolean;
  enableActions?: boolean;
  onExpandClick: () => void;
  options?: ActionOption[];
}

const Header = ({
  id,
  title,
  opened = true,
  isTitlePlate = false,
  enableActions = false,
  onExpandClick,
  options,
}: Props) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const optionsWrapperRef = useRef<HTMLUListElement>(null);
  const { isDropdownOpened, changeDropdownState, onKeyDown } = useDropdown(
    buttonRef,
    optionsWrapperRef
  );

  const onOptionClick = (clicked: ActionOption) => {
    clicked.onClick();
    changeDropdownState(false);
  };

  const content = (
    <ul
      ref={optionsWrapperRef}
      className={styles.cardHeaderActionsTooltipOptionsContainer}
    >
      {options?.map((action) => (
        <Button
          id={`${id}-${action.value}-action-button`}
          key={`${id}-${action.value}-action-button`}
          className={styles.cardHeaderActionsTooltipAction}
          disabled={action?.disabled}
          onClick={() => onOptionClick(action)}
        >
          {action.label}
        </Button>
      ))}
    </ul>
  );

  return (
    <div className={styles.cardHeader}>
      <div className={styles.cardHeaderLeft}>
        <IconButton
          id={`${id}-card-header-expand-btn`}
          aria-label="Collapse Button"
          onClick={onExpandClick}
          className={styles.cardHeaderCollapseButton}
        >
          {opened ? (
            <ArrowUp fillColor={null} />
          ) : (
            <ArrowDown fillColor={null} />
          )}
        </IconButton>
        {title &&
          (isTitlePlate ? (
            <InfoPlate className={styles.cardHeaderTitlePlate} variant="dark">
              {title}
            </InfoPlate>
          ) : (
            <span className={styles.cardHeaderTitleText}>{title}</span>
          ))}
      </div>

      {enableActions && options?.length && (
        <div className={styles.cardHeaderRight}>
          <Tippy
            interactive
            placement="bottom-start"
            arrow={false}
            visible={isDropdownOpened}
            content={content}
            className={styles.cardHeaderActionsTooltip}
          >
            <IconButton
              id={`${id}-actions-button`}
              ref={buttonRef}
              onKeyDown={onKeyDown}
              onClick={() => changeDropdownState(!isDropdownOpened)}
              aria-label={t("more")}
            >
              <MoreIcon />
            </IconButton>
          </Tippy>
        </div>
      )}
    </div>
  );
};

export default Header;
